import axios from 'axios';
import Cookies from 'universal-cookie';
import queryString from 'query-string';
import { getCookie, removeCookie, setCookie, setLocalStorage, removeLocalStorage, removeSessionStorage, createLocalStorageSync } from '../components/utilities/functions';
import flagsmith from 'flagsmith/isomorphic';
import { IdxStatus, AuthenticatorKey } from '@okta/okta-auth-js';
import { useOktaAuth } from './use-okta-auth';
import { useCart } from './use-cart';
import { navigate } from 'gatsby';
import { eloquaTrackingUrl } from '../components/utilities/constants';
import { HandleEloquaError } from './utils/eloquaError';
import { HandleAxiosError } from './utils/axiosError';
import { useProduct } from './use-product';
import { useDispatch } from 'react-redux';

export const useAccount = () => {
	const { oktaAuth, authState, setAuthState, setProfile, setRedirect } = useOktaAuth();
	const { getCart } = useCart();
	const { fetchProductTiles } = useProduct();
	const dispatch = useDispatch();
	const pellaAPIURL = process.env.GATSBY_PELLA_API;
	const getCookieContext = () => new Cookies();
	let params = {};
	const cartSync = createLocalStorageSync('cartSession');

	const register = async (accountInfo, redirect = null) => {
		const cookies = getCookieContext();
		const referrerAction = cookies.get('CreateAccountReferrer');
		const eloquaCookieValue = cookies.get('ELOQUA');
		let guid = '';

		if (eloquaCookieValue) {
			const keyValues = eloquaCookieValue.split('&');

			if (keyValues) {
				for (const keyValue of keyValues) {
					const pair = keyValue.split('=');

					if (pair[0].toLowerCase() === 'guid') {
						guid = pair[1];

						break;
					}
				}
			}
		}

		try {
			const user = await axios.post(`${pellaAPIURL}/account/v2/register`, accountInfo);

			if (user.data.id) {
				const loginInfo = {
					username: accountInfo.email,
					password: accountInfo.password,
				};

				try {
					const eloquaConfig = {
						headers: {
							'Content-Type': 'application/x-www-form-urlencoded',
						},
					};
					const eloquaBody = {
						elqFormName: 'create-an-account',
						elqSiteID: '1920192983',
						elqCustomerGUID: guid ? guid : '',
						elqCookieWrite: '0',
						userEmail: accountInfo.email,
						fname: accountInfo.firstName,
						lname: accountInfo.lastName,
						proCheckBox: accountInfo.customerType,
						termsCheckBox: accountInfo.termsAgreement,
						proCompany: accountInfo.companyName || '',
						proFocus: accountInfo.businessFocus || '',
						proPhone: accountInfo.phone || '',
						proEmployees: '',
						proProjects: '',
						proWorkType: '',
						proWebsite: '',
						urldomain: referrerAction || '',
						env: process.env.GATSBY_CONTENTSTACK_ENVIRONMENT,
					};

					await axios.post(eloquaTrackingUrl, queryString.stringify(eloquaBody), eloquaConfig);

					cookies.remove('CreateAccountReferrer');
				} catch (error) {
					HandleEloquaError(error);
				}

				const signIn = await login(loginInfo);

				if (redirect) {
					return navigate(redirect, {
						state: { redirectURL: redirect, hasRedirect: true },
					});
				} else {
					// This returns a signIn.success property
					return signIn;
				}
			} else {
				return user;
			}
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const login = async (user) => {
		const cookies = getCookieContext();
		try {
			let loginResponse;

			await beginTransaction();

			if (user?.status === IdxStatus.SUCCESS) {
				loginResponse = user;
			} else {
				const session = await oktaAuth.session.get();

				if (session?.status && session.status !== 'INACTIVE') {
					try {
						await oktaAuth.session.close();
					} catch {}
				}

				loginResponse = await oktaAuth.idx.authenticate({
					username: user.username,
					password: user.password,
				});
			}

			if (loginResponse.status === IdxStatus.SUCCESS) {
				const profileContext = loginResponse.context.user.value.profile;

				const customerType = await getCustomerType(loginResponse.tokens.accessToken.accessToken);

				if (customerType.customerType === 'professional') {
					const date = new Date();

					setCookie('expire_modal', date.setDate(date.getDate() + 30), 3600 * 24 * 365);
				}

				try {
					let customer = await fetchCustomer(loginResponse.tokens.accessToken.accessToken);

					if (customer.statusCode === 404) {
						customer = await generateCustomer(profileContext.firstName, profileContext.lastName, loginResponse.tokens.accessToken.accessToken);

						if (!customer?.Customer?.id) {
							throw Error('The customer record was not created in the cart-order-mgmt API');
						}
					} else if (!customer.Customer) {
						throw Error('There was an error retrieving this customer record in the cart-order-mgmt API');
					}

					try {
						flagsmith.identify(loginResponse.context.user.value.identifier, { name: customer.Customer.name, type: customerType.customerType });
					} catch (error) {
						// eslint-disable-next-line no-console -- allow warn here
						console.warn('fs-log', error);
					}

					try {
						if (window.location?.pathname?.indexOf('/checkout/') === -1 && window.location?.pathname?.indexOf('/cart/') === -1) {
							await getCart(loginResponse.tokens.accessToken.accessToken);
						}
					} catch {}

					const guid = cookies.get('ELOQUA');
					const guidValue = guid != null ? guid.split('=').pop() : '';

					if (customerType.customerType && customer.Customer.email && guidValue) {
						try {
							const eloquaConfig = {
								headers: {
									'Content-Type': 'application/x-www-form-urlencoded',
								},
							};

							const eloquaBody = {
								elqFormName: 'login',
								elqSiteID: '1920192983',
								elqCustomerGUID: guidValue,
								elqCookieWrite: '0',
								email: customer.Customer.email,
								username: loginResponse.context.user.value.identifier,
								customerType: customerType.customerType,
								env: process.env.GATSBY_CONTENTSTACK_ENVIRONMENT,
							};

							await axios.post(eloquaTrackingUrl, queryString.stringify(eloquaBody), eloquaConfig);
						} catch (error) {
							HandleEloquaError(error);
						}
					}
				} catch (error) {
					HandleEloquaError(error);
				}

				await oktaAuth.tokenManager.setTokens(loginResponse.tokens);

				const profileValue = {
					date: Date.now(),
					...profileContext,
					identifier: loginResponse.context.user.value.identifier,
					isPro: customerType.customerType === 'professional' ? true : false,
				};

				setLocalStorage('authSession', profileValue);
				setProfile(profileValue);

				if (user?.redirectOverride) {
					setRedirect(user.redirectOverride);
				}

				return { success: true, isPro: profileValue.isPro };
			} else {
				try {
					await oktaAuth.idx.cancel();
				} catch {}

				const locked = loginResponse?.messages?.some((item) => item?.i18n?.key === 'errors.E0000119' || item?.message?.includes('Your account is locked.')) || false;
				let forbidden = loginResponse?.messages?.some((item) => item?.i18n?.key === 'idx.error.code.user_not_assigned' || item?.message?.includes('You are not allowed to access this app.')) || false;

				if (loginResponse?.nextStep?.name === 'redirect-idp') {
					forbidden = true;
				}

				let message = 'There was a problem logging in. Please check your username or password and try again.';

				if (locked) {
					message = 'Your account has been locked from repeated failed login attempts. Please email our customer service team at shop@pella.com for assistance accessing your account.';
				} else if (forbidden) {
					message = 'Your Pella account is registered for use on portal.mypella.com. You will not be able to use this account on pella.com.';
				}

				return {
					success: false,
					alert: {
						message,
					},
					locked,
					forbidden,
				};
			}
		} catch (error) {
			try {
				await oktaAuth.idx.cancel();
			} catch (error) {
				return HandleAxiosError(error); // probably needs renamed to ErrorHandling()
			}
			return HandleAxiosError(error); // probably needs renamed to ErrorHandling()
		}
	};

	const logout = async () => {
		if (getCookie('expire_modal')) {
			removeCookie('expire_modal');
		}

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'logout',
		});

		removeLocalStorage('authSession');
		cartSync.set({});
		removeSessionStorage('productsFetched');

		setProfile({
			date: Date.now(),
			isPro: false,
		});
		setAuthState({});
		cartSync.set({});

		const [, , session] = await Promise.all([oktaAuth.revokeRefreshToken(), oktaAuth.revokeAccessToken(), oktaAuth.session.get()]);

		if (session?.status !== 'INACTIVE') {
			try {
				await oktaAuth.session.close();
			} catch {}
		}

		oktaAuth.tokenManager.clear();
		dispatch(fetchProductTiles());

		navigate('/account/login/');
	};

	const getCustomerType = async (token) => {
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			};

			const customerType = await axios.get(`${pellaAPIURL}/account/v2/user-type`, config);

			return customerType.data;
		} catch (error) {
			if (error.response && error.response.data) {
				return error.response.data;
			} else {
				return { success: false };
			}
		}
	};

	const fetchCustomer = async (token = null) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${token || authState.accessToken.accessToken}`,
				},
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const customer = await axios.get(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers`, { ...config, params: params });

			return customer.data;
		} catch (error) {
			if (error.response && error.response.data) {
				return error.response.data;
			} else {
				return { success: false };
			}
		}
	};

	const getUser = async (token = null) => {
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${token || authState.accessToken.accessToken}`,
				},
			};

			const response = await axios.get(`${pellaAPIURL}/account/v2/user`, config);

			const profileValue = {
				date: Date.now(),
				...response.data.profile,
				identifier: response.data.profile.email,
				isPro: response.data.customerType === 'professional' ? true : false,
			};

			setLocalStorage('authSession', profileValue);
			setProfile(profileValue);

			return response;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const generateCustomer = async (firstname, lastname, token) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			};

			const body = {
				firstname: firstname,
				lastname: lastname,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const customer = await axios.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers`, body, { ...config, params: params });

			return customer.data;
		} catch (error) {
			if (error.response && error.response.data) {
				return error.response.data;
			} else {
				return { success: false };
			}
		}
	};

	const updateUser = async (accountInfo) => {
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${authState.accessToken.accessToken}`,
				},
			};

			const cookies = getCookieContext();
			const eloquaCookieValue = cookies.get('ELOQUA');

			if (eloquaCookieValue) {
				accountInfo.elqCustomerGUIDCookie = eloquaCookieValue;
			}

			const response = await axios.put(`${pellaAPIURL}/account/v2/user`, accountInfo, config);

			if (response.data.profile) {
				const profileValue = {
					date: Date.now(),
					...response.data.profile,
					identifier: response.data.profile.email,
					isPro: response.data.customerType === 'professional' ? true : false,
				};

				setLocalStorage('authSession', profileValue);
				setProfile(profileValue);

				try {
					flagsmith.identify(response.data.profile.email, { name: `${response.data.profile.firstName} ${response.data.profile.lastName}`, type: response.data.customerType });
				} catch (error) {
					// eslint-disable-next-line no-console -- allow warn here
					console.warn('fs-log', error);
				}
			}

			return response;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const changePassword = async (newPassword) => {
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${authState.accessToken.accessToken}`,
				},
			};

			const response = await axios.put(
				`${pellaAPIURL}/account/v2/user`,
				{
					password: newPassword,
				},
				config
			);

			if (response.data.profile) {
				const profileValue = {
					date: Date.now(),
					...response.data.profile,
					identifier: response.data.profile.email,
					isPro: response.data?.customerType === 'professional' ? true : false,
				};

				setLocalStorage('authSession', profileValue);
				setProfile(profileValue);
			}

			return response;
		} catch (error) {
			if (error.response && error.response.data) {
				return error.response;
			} else {
				return { success: false };
			}
		}
	};

	const forgotPassword = async (username) => {
		try {
			await beginTransaction();

			const recover = await oktaAuth.idx.recoverPassword({
				username,
				authenticators: [AuthenticatorKey.OKTA_EMAIL],
			});

			let message = 'Unable to fulfill your request. Please try again later.';
			let isError = false;

			if (recover.status === IdxStatus.PENDING && recover.nextStep.name === 'authenticator-verification-data') {
				const proceed = await oktaAuth.idx.proceed({ methodType: 'email' });

				if (proceed.status === IdxStatus.PENDING && proceed.nextStep.name === 'challenge-authenticator') {
					return { success: true };
				}

				message = proceed.messages?.[0]?.message || message;
				isError = true;
			} else {
				message = recover.messages?.[0]?.message || message;
			}

			try {
				await oktaAuth.idx.cancel();
			} catch {}

			const locked = recover?.messages?.some((item) => item?.i18n?.key === 'errors.E0000119' || item?.message?.includes('Your account is locked.')) || false;

			return {
				success: false,
				[isError ? 'error' : 'alert']: {
					message: locked ? 'Your account has been locked from repeated failed login attempts. Please email our customer service team at shop@pella.com for assistance accessing your account.' : message,
				},
				locked,
			};
		} catch (error) {
			// eslint-disable-next-line no-console -- allow error here
			console.error(error);

			try {
				await oktaAuth.idx.cancel();
			} catch {}

			return {
				success: false,
				error: {
					message: 'An unknown error occurred.',
				},
			};
		}
	};

	const resendOneTimePassword = async () => {
		try {
			const resend = await oktaAuth.idx.proceed({ resend: true });

			if (resend.status === IdxStatus.PENDING && resend.nextStep.name === 'challenge-authenticator') {
				return { success: true };
			}

			let message = resend.messages?.[0]?.message || '.';

			if (/Your\ session\ has\ expired/i.test(message)) {
				message = 'Your session has expired.';
			}

			return {
				success: false,
				alert: {
					message,
				},
			};
		} catch (error) {
			// eslint-disable-next-line no-console -- allow error here
			console.error(error);

			try {
				await oktaAuth.idx.cancel();
			} catch {}
			return {
				success: false,
				error: {
					message: 'Your session has expired.',
				},
			};
		}
	};

	const verifyResetCode = async (code) => {
		try {
			const verification = await oktaAuth.idx.proceed({ verificationCode: code });

			if (verification.status === IdxStatus.PENDING && verification.nextStep.name === 'reset-authenticator') {
				return { success: true };
			}

			let message = verification.messages?.[0]?.message || '.';

			if (/Your\ session\ has\ expired/i.test(message)) {
				message = 'Your session has expired.';
			} else if (/invalid\ code/i.test(message)) {
				message = 'Incorrect one time password.';
			}

			return {
				success: false,
				alert: {
					message,
				},
			};
		} catch (error) {
			// eslint-disable-next-line no-console -- allow error here
			console.error(error);

			try {
				await oktaAuth.idx.cancel();
			} catch {}
			return {
				success: false,
				error: {
					message: 'Your session has expired.',
				},
			};
		}
	};

	const resetPassword = async (newPassword, redirect = undefined) => {
		try {
			const reset = await oktaAuth.idx.proceed({ password: newPassword });

			if (reset.status === IdxStatus.SUCCESS) {
				if (reset?.context?.user?.value?.identifier) {
					reset.redirectOverride = redirect;

					const loginRequest = await login(reset);

					if (loginRequest.success) {
						return loginRequest;
					}
				}

				return {
					success: true,
					alert: {
						message: 'Password successfully updated.',
					},
				};
			}

			let message = reset.messages?.[0]?.message || 'Unable to reset your password, please try again later.';

			if (/Your\ session\ has\ expired/i.test(message)) {
				message = 'Your session has expired.';
			}

			return {
				success: false,
				alert: {
					message,
				},
			};
		} catch (error) {
			// eslint-disable-next-line no-console -- allow error here
			console.error(error);

			try {
				await oktaAuth.idx.cancel();
			} catch {}
			return {
				success: false,
				error: {
					message: 'Your session has expired.',
				},
			};
		}
	};

	const beginTransaction = async () => {
		try {
			await oktaAuth.idx.cancel();
		} catch {}

		oktaAuth.transactionManager.clear();
	};

	return {
		register,
		login,
		logout,
		getCustomerType,
		fetchCustomer,
		getUser,
		updateUser,
		changePassword,
		forgotPassword,
		resendOneTimePassword,
		verifyResetCode,
		resetPassword,
	};
};
